import { OptionType } from '~/features/types';
import { specialtiesToOptions } from '~/features/vault/aoi/utils';
import { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { getData } from '~/utils/service';
import { subspecialtiesToOptions, subspecialtyFieldsToApi } from '../../../subspecialties';
import { BasicInfoFormValues } from './types';

export const loadAOIOptionByProviderCategory = async (
  providerCategoryId: number,
  searchTerm?: string
): Promise<OptionType[]> => {
  if (!providerCategoryId) {
    return [];
  }
  return await getData<OptionType[]>('aoisOptionsByProviderCategory', {
    pathParams: { providerCategoryId: providerCategoryId.toString() },
    params: { specialty_name: searchTerm },
    transformResponse: specialtiesToOptions,
  });
};

export const transformBasicInfoFormToApi = (formData: BasicInfoFormValues): string => {
  // The form's option objects are not the API's `subspecialties`; only the ids go out.
  const { subspecialties, generalSpecialtyFlag, ...rest } = formData;
  return JSON.stringify({
    ...rest,
    ...subspecialtyFieldsToApi(subspecialties, generalSpecialtyFlag),
    providerCategoryId: formData.providerCategory.value,
    specialtyName: formData.aoiSpecialty.label,
    aoiSpecialtyId:
      formData.aoiSpecialty.value.trim() === '' ? null : Number(formData.aoiSpecialty.value),
    workLocation: formData.workLocation.value,
  } as Omit<
    MsaasJobListing,
    | 'id'
    | 'employer'
    | 'facilities'
    | 'details'
    | 'negotiations'
    | 'employerId'
    | 'stateCodes'
    | 'subspecialties'
  > &
    ReturnType<typeof subspecialtyFieldsToApi>);
};

// [MCV-1889] An update may not carry the side (409 IMMUTABLE_FIELD), so only a create sends it.
export const transformBasicInfoFormToUpdateApi = (formData: BasicInfoFormValues): string => {
  const { listingSide: _listingSide, ...rest } = formData;
  return transformBasicInfoFormToApi(rest as BasicInfoFormValues);
};

export const transformApiToBasicInfoForm = (apiData: MsaasJobListing): BasicInfoFormValues => {
  return {
    id: apiData.id,
    listingTitle: apiData.listingTitle,
    listingDescription: apiData.listingDescription,
    providerCategory: { label: '', value: apiData.providerCategoryId },
    aoiSpecialty:
      apiData.aoiSpecialtyId && apiData.specialtyName
        ? { label: apiData.specialtyName, value: apiData.aoiSpecialtyId.toString() }
        : { label: '', value: '' },
    subspecialties: subspecialtiesToOptions(apiData.subspecialties),
    generalSpecialtyFlag: apiData.generalSpecialtyFlag ?? true,
    isRenewable: apiData.isRenewable,
    listingSide: apiData.listingSide,
    workLocation: {
      label: apiData.workLocation.charAt(0) + apiData.workLocation.slice(1).toLowerCase(),
      value: apiData.workLocation,
    },
  };
};
